import { useState, useCallback, useRef } from 'react';

export const useLoadingStates = () => {
  const [loadingStates, setLoadingStates] = useState(new Map());
  const timeoutsRef = useRef(new Map());

  // Set loading state for a specific action
  const setLoading = useCallback((actionId, isLoading, message = '') => {
    setLoadingStates(prev => {
      const newStates = new Map(prev);
      if (isLoading) {
        newStates.set(actionId, {
          isLoading: true,
          message,
          startTime: Date.now()
        });
      } else {
        newStates.delete(actionId);
      }
      return newStates;
    });
  }, []);

  // Set loading with auto-timeout
  const setLoadingWithTimeout = useCallback((actionId, message = '', timeout = 30000) => {
    setLoading(actionId, true, message);

    // Clear existing timeout
    if (timeoutsRef.current.has(actionId)) {
      clearTimeout(timeoutsRef.current.get(actionId));
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      setLoading(actionId, false);
      timeoutsRef.current.delete(actionId);
    }, timeout);

    timeoutsRef.current.set(actionId, timeoutId);
  }, [setLoading]);

  // Clear specific loading state
  const clearLoading = useCallback((actionId) => {
    setLoading(actionId, false);

    // Clear timeout if exists
    if (timeoutsRef.current.has(actionId)) {
      clearTimeout(timeoutsRef.current.get(actionId));
      timeoutsRef.current.delete(actionId);
    }
  }, [setLoading]);

  // Clear all loading states
  const clearAllLoading = useCallback(() => {
    setLoadingStates(new Map());

    // Clear all timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current.clear();
  }, []);

  // Check if specific action is loading
  const isLoading = useCallback((actionId) => {
    return loadingStates.has(actionId) && loadingStates.get(actionId)?.isLoading;
  }, [loadingStates]);

  // Check if any action is loading
  const isAnyLoading = useCallback(() => {
    return Array.from(loadingStates.values()).some(state => state.isLoading);
  }, [loadingStates]);

  // Get loading message for specific action
  const getLoadingMessage = useCallback((actionId) => {
    const state = loadingStates.get(actionId);
    return state?.message || '';
  }, [loadingStates]);

  // Get loading duration for specific action
  const getLoadingDuration = useCallback((actionId) => {
    const state = loadingStates.get(actionId);
    if (!state || !state.startTime) {return 0;}
    return Date.now() - state.startTime;
  }, [loadingStates]);

  // Get all active loading states
  const getActiveLoadingStates = useCallback(() => {
    const activeStates = [];
    loadingStates.forEach((state, actionId) => {
      if (state.isLoading) {
        activeStates.push({
          actionId,
          message: state.message,
          duration: Date.now() - state.startTime
        });
      }
    });
    return activeStates;
  }, [loadingStates]);

  // Wrapper for async operations with loading state
  const withLoading = useCallback(async (actionId, asyncOperation, message = '') => {
    try {
      setLoading(actionId, true, message);
      const result = await asyncOperation();
      return result;
    } finally {
      clearLoading(actionId);
    }
  }, [setLoading, clearLoading]);

  // Cleanup function
  const cleanup = useCallback(() => {
    clearAllLoading();
  }, [clearAllLoading]);

  return {
    setLoading,
    setLoadingWithTimeout,
    clearLoading,
    clearAllLoading,
    isLoading,
    isAnyLoading,
    getLoadingMessage,
    getLoadingDuration,
    getActiveLoadingStates,
    withLoading,
    cleanup,

    // Direct access to states for advanced use cases
    loadingStates
  };
};

export default useLoadingStates;
